import { HydratedWorkerTeam } from '@scale/shared/workerTeams/types';
import React, {
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
  MouseEvent,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { useToggle } from 'react-use-v13';
import { CircularProgress } from 'react-scale/components/design-system';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'design-system';
// eslint-disable-next-line restrict-imports/restrict-imports
import Switch from '@material-ui/core/Switch';
import { useGetWorkerTeamDataTree } from '../hooks';
import { Skeleton } from '@scale/scaleui';

interface TeamExplorerProps {
  teamId: string;
  isManager?: boolean;
  TeamSelectButton?: () => JSX.Element;
  onSelectTeam?: (teamId: string, teamName: string, unabbreviatedTeamName: string) => void;
}
function getArray<T>(length: number, fn: (i: number) => T): T[] {
  return Array.from({ length }, (_, i) => fn(i));
}
const LoadingTemplate = () => (
  <div className="scaleui scale-theme p-2 flex gap-2">
    <div className="flex-1">
      <Skeleton className="w-full p-3" />
    </div>
    <div className="flex-1 flex flex-col gap-2">
      {getArray(4, i => (
        <Skeleton className="w-full p-3" key={i} />
      ))}
    </div>
    <div className="flex-1 flex flex-col gap-2 ">
      {getArray(4, i => (
        <Skeleton className="w-full p-3" key={i} />
      ))}
    </div>
  </div>
);
export const TeamExplorer: FC<TeamExplorerProps> = ({
  teamId: _teamId,
  isManager,
  TeamSelectButton,
  onSelectTeam,
}) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [teamId, setTeamId] = useState(_teamId);

  const [cachedTeamsData, setCachedTeamsData] = useState<Record<string, HydratedWorkerTeam>>({});

  let history: any = undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    history = useHistory();
  } catch {
    // do nothing
  }
  const selectTeamId = useCallback(
    (id: string) => {
      if (history) history.replace(history.location.pathname.replace(_teamId, id));
    },
    [history, _teamId],
  );

  // this hook has a recursive fn
  const { data, isLoading } = useGetWorkerTeamDataTree({
    teamId,
    cacheTeamsIds: _.keys(cachedTeamsData),
  });
  useEffect(() => {
    if (_.keys(cachedTeamsData).length === 0 && data) {
      setCachedTeamsData(data);
    } else if (data) {
      for (const id in data) {
        if (!(id in cachedTeamsData)) {
          setCachedTeamsData(prev => ({ ...prev, [id]: data[id] }));
        }
      }
    }
  }, [cachedTeamsData, data, isLoading]);

  const ancestors = useMemo(() => {
    let cur: string | undefined = teamId;
    const result: HydratedWorkerTeam[] = [];
    const cacheIdKeys = _.keys(cachedTeamsData);
    if (cacheIdKeys.length && !cacheIdKeys.includes(cur)) {
      cur = _.find(cacheIdKeys, t =>
        _.includes(
          cachedTeamsData[t].subTeams.map(k => k.id),
          cur,
        ),
      );
    }
    while (cur && cur in cachedTeamsData) {
      result.push(cachedTeamsData[cur]);
      cur = cachedTeamsData[cur].parentTeamId;
    }

    return _.reverse(result);
  }, [cachedTeamsData, teamId]);

  const tableData: {
    id: string;
    highlighted: boolean;
    selected: boolean;
    name: string;
    subTeams: HydratedWorkerTeam[];
  }[][] = useMemo(() => {
    if (ancestors.length === 0) return [];
    const columns = [[ancestors[0]], ...ancestors.map(team => team.subTeams)];

    return columns
      .map((column, i) =>
        _.sortBy(column, 'name').map(t => ({
          // convert team to node
          id: t.id,
          highlighted: t.id === ancestors[i]?.id,
          selected: t.id === teamId,
          name: t.name,
          subTeams: (t as any).subTeams,
        })),
      )
      .filter(column => column.length > 0);
  }, [ancestors, teamId]);
  const handleClickNode = useCallback((e: MouseEvent<HTMLDivElement>, teamId: string) => {
    setTeamId(teamId);
  }, []);

  // After selecting a team, scroll all the way to the right of the container to show the team's child teams
  // useLayoutEffect is needed to correctly scroll after the child teams render
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const left = container.scrollWidth;
    container.scrollTo({
      left,
      behavior: 'smooth',
    });
  }, [tableData]);

  const abbreviate = (text: string) => {
    if (text.length > 30) {
      return `...${text.substring(text.length - 27)}`;
    }
    return text;
  };

  const handleKeyDown: React.KeyboardEventHandler = useCallback(
    e => {
      const teams = cachedTeamsData;
      const cur = teams[teamId];
      const par = cur?.parentTeamId ? teams[cur?.parentTeamId] : undefined;
      const sortedSib = par ? _.sortBy(par.subTeams, 'name') : undefined;
      const idx = par ? _.findIndex(sortedSib, t => t.id === teamId) : -1;
      if (e.key === 'ArrowLeft') {
        par && setTeamId(par.id);
      } else if (e.key === 'ArrowRight') {
        cur?.subTeams[0] && setTeamId(cur.subTeams[0].id);
      } else if (e.key === 'ArrowUp') {
        par && sortedSib && idx > 0 && setTeamId(sortedSib[idx - 1].id);
      } else if (e.key === 'ArrowDown') {
        par && sortedSib && idx < par.subTeams.length - 1 && setTeamId(sortedSib[idx + 1].id);
      } else return;
      e.stopPropagation();
      e.preventDefault();
    },
    [teamId, cachedTeamsData],
  );
  const [editMode, toggleEditMode] = useToggle(false);

  if (isLoading && ancestors.length === 0) {
    return <LoadingTemplate />;
  }
  return (
    <div tabIndex={0} className={classes.container} onKeyDown={handleKeyDown} ref={containerRef}>
      {tableData
        .filter(column => column.length > 0)
        .map((column, i) => (
          <div className={classes.column} key={i}>
            {column.map(({ highlighted, selected, name, id, subTeams }) => (
              <div
                key={id}
                className={selected && !!subTeams.length && isLoading ? classes.row : ''}
              >
                <div
                  key={id}
                  className={clsx(classes.node, {
                    highlighted,
                    selected,
                  })}
                  draggable={editMode}
                  onClick={e => handleClickNode(e, id)}
                >
                  <div>
                    <span className="icon">
                      <FontAwesomeIcon
                        icon={subTeams && subTeams.length < 1 ? 'users' : 'folder'}
                      />
                    </span>
                    <Tooltip arrow placement={'top'} title={name} enterDelay={1000}>
                      <span className="text">{abbreviate(name)}</span>
                    </Tooltip>
                  </div>
                  <span
                    className="icon"
                    onClick={() =>
                      onSelectTeam ? onSelectTeam(id, abbreviate(name), name) : selectTeamId(id)
                    }
                  >
                    {TeamSelectButton ? (
                      <TeamSelectButton />
                    ) : (
                      <FontAwesomeIcon icon="external-link" />
                    )}
                  </span>
                </div>
                {selected && !!subTeams.length && isLoading ? (
                  <span className={classes.loadingIcon}>
                    <CircularProgress size={16} />
                  </span>
                ) : null}
              </div>
            ))}
            {editMode && i === tableData.length - 1 && (
              <button>Create Subteam (coming soon?)</button>
            )}
          </div>
        ))}
      {isManager && (
        <label htmlFor="switch">
          <Switch id="switch" value={editMode} onChange={toggleEditMode} /> Edit Mode (coming soon)
        </label>
      )}
    </div>
  );
};
const useStyles = makeStyles({
  container: {
    display: 'flex',
    overflow: 'auto',
    outline: 'none',
    minWidth: 1,
    '&:focus .selected': {
      backgroundColor: '#2564D9',
      color: 'white',
    },
  },
  column: {
    display: 'flex',
    flexBasis: 320,
    flexShrink: 0,
    flexDirection: 'column',
    overflow: 'auto',
    padding: '8px 20px',
    border: '1px solid #E6E6E6',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 20px',
    alignItems: 'center',
    width: '100%',
    gap: 2,
    '&:hover': {
      backgroundColor: '#F1F1F1',
    },
  },
  loadingIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    width: '100%',
    height: '100%',
  },
  node: {
    display: 'flex',
    padding: '8px 12px',
    cursor: 'pointer',
    justifyContent: 'space-between',
    '&.highlighted': {
      backgroundColor: '#DCDDDC',
    },
    '&.selected': {
      backgroundColor: 'gray',
      color: 'white',
    },
    '& .icon': {
      color: '#8CD0F7',
      marginRight: 4,
      minWidth: 12,
      width: 12,
    },
    '& .text': {
      overflow: 'auto',
    },
  },
});
